import { CircleAlert, History, Loader2, WifiOff } from "lucide-react";
import { Button } from "../ui/button";
import { RemoteInventoryRecovery } from "./RemoteInventoryRecovery";
import {
  ComposerBannerStack,
  type ComposerBannerStackItem,
} from "./composer-notices/ComposerBannerStack";
import type { RemoteRuntimeConnectionState } from "./remoteRuntimeThreadConnection.logic";
import type { RemoteThreadEnvironmentWaitingReason } from "./useRemoteThreadExecutionIntent";

export function RemoteThreadNotices(props: {
  readonly connectionState: RemoteRuntimeConnectionState;
  readonly runtimeName: string;
  readonly staleMessage?: string | undefined;
  readonly onRetry: () => void;
  readonly onRefreshHistory?: (() => void) | undefined;
  readonly hasSubmission?: boolean;
  readonly environmentWaitingReason?: RemoteThreadEnvironmentWaitingReason | null;
  readonly usageNotice?: ComposerBannerStackItem | null;
}) {
  const items: ComposerBannerStackItem[] = [];
  const connection = props.connectionState;
  if (connection.kind === "offline") {
    items.push({
      id: "connection",
      variant: "warning",
      icon: <WifiOff />,
      title: `${props.runtimeName} is offline, showing saved history`,
      actions: (
        <Button
          className="min-h-12"
          size="sm"
          variant="ghost"
          render={<a href="/settings/environments" />}
        >
          Connections
        </Button>
      ),
    });
  } else if ("message" in connection && !(connection.kind === "failed" && props.hasSubmission)) {
    items.push({
      id: "connection",
      variant: "error",
      priority: "urgent",
      icon: <CircleAlert />,
      title: connection.message,
      actions:
        connection.kind === "failed" ? (
          <Button
            type="button"
            className="min-h-12"
            size="sm"
            variant="ghost"
            onClick={props.onRetry}
          >
            Retry
          </Button>
        ) : undefined,
    });
  }
  if (props.environmentWaitingReason === "updating") {
    items.push({
      id: "environment-updating",
      variant: "default",
      priority: "activity",
      icon: <Loader2 className="animate-spin motion-reduce:animate-none" />,
      title: `${props.runtimeName} is updating`,
      description: "Your message sends when the update finishes.",
    });
  }
  if (
    !props.hasSubmission &&
    !props.environmentWaitingReason &&
    (connection.kind === "connecting" || connection.kind === "retrying")
  ) {
    items.push({
      id: "preparing",
      variant: "default",
      priority: "activity",
      icon: <Loader2 className="animate-spin motion-reduce:animate-none" />,
      title: `Starting ${props.runtimeName}…`,
    });
  }
  if (props.staleMessage) {
    items.push({
      id: "history",
      variant: "warning",
      icon: <History />,
      title: `History may be out of date. ${props.staleMessage}`,
      actions: (
        <Button
          type="button"
          className="min-h-12"
          size="sm"
          variant="ghost"
          onClick={props.onRefreshHistory ?? props.onRetry}
        >
          Retry
        </Button>
      ),
    });
  }
  if (props.usageNotice) items.push(props.usageNotice);
  if (!items.length) return connection.kind === "checking" ? <RemoteInventoryRecovery /> : null;
  return <ComposerBannerStack items={items} testId="remote-thread-connection-strip" />;
}
